import { useQuery } from '@tanstack/react-query'
import { todayRowsQuery, type TodayRow } from '../api/overview'
import { Content } from '../ui/Shell'
import { hrefOf } from './href'

/**
 * Overview::today - macheta today/index.php, element cu element.
 * Cifrele vin din GET /api/v2/overview/today. Legaturile moarte
 * (listele Classic) nu se deseneaza ca <a>.
 */

const TODAY_STYLE = `
.od-today { padding: 4px 4px 32px; max-width: 900px; }
.od-today-lead { margin: 0 0 18px; font-size: 15px; color: var(--od-ink-soft, #5b6672); }
.od-today-lead b { color: var(--od-ink, #1f2933); font-size: 17px; }
.od-today-clear { display: flex; gap: 12px; align-items: flex-start; padding: 16px 18px; margin: 0 0 20px; background: var(--od-ok-bg, #e4f2e4); border-left: 3px solid var(--od-ok, #008300); font-size: 13.5px; color: var(--od-ink, #1f2933); }
.od-today-clear .fas { color: var(--od-ok, #008300); font-size: 18px; margin-top: 1px; }
.od-today-rows { display: flex; flex-direction: column; gap: 8px; }
.od-today-row { display: flex; align-items: center; gap: 14px; padding: 12px 16px; border: 1px solid var(--od-rule, #d9dee4); border-left: 3px solid var(--od-rule, #d9dee4); border-radius: var(--od-radius, 3px); background: var(--od-surface, #fff); text-decoration: none; color: var(--od-ink, #1f2933); transition: box-shadow 140ms ease; }
.od-today-row:hover, .od-today-row:focus { box-shadow: var(--od-shadow, 0 1px 2px rgba(31, 41, 51, .08)); text-decoration: none; color: var(--od-ink, #1f2933); }
.od-today-icon { flex: 0 0 auto; width: 20px; text-align: center; color: var(--od-ink-faint, #6b7480); }
.od-today-n { flex: 0 0 62px; text-align: right; font-family: var(--od-font-mono, monospace); font-size: 22px; font-weight: 600; font-variant-numeric: tabular-nums; line-height: 1; }
.od-today-text { flex: 1 1 auto; min-width: 0; }
.od-today-label { display: block; font-size: 14px; font-weight: 600; }
.od-today-about { display: block; margin-top: 2px; font-size: 12px; line-height: 1.4; color: var(--od-ink-soft, #5b6672); }
.od-today-go { flex: 0 0 auto; color: var(--od-ink-faint, #6b7480); font-size: 12px; }
.od-today-row.is-crit { border-left-color: var(--od-crit, #b42318); }
.od-today-row.is-crit .od-today-n { color: var(--od-crit, #b42318); }
.od-today-row.is-warn { border-left-color: var(--od-warn, #9e6b00); }
.od-today-row.is-warn .od-today-n { color: var(--od-warn, #9e6b00); }
.od-today-row.is-info { border-left-color: var(--od-info, #1f62cb); }
.od-today-row.is-info .od-today-n { color: var(--od-info, #1f62cb); }
.od-today-row.is-clear .od-today-n { color: var(--od-ink-faint, #6b7480); }
.od-today-row.is-unknown .od-today-n { color: var(--od-ink-faint, #6b7480); }
`

export function todayState(row: TodayRow): string {
  if (row.count === null) return 'unknown'
  return row.count > 0 ? row.tone : 'clear'
}

/*
 * Un rand fara cifra (`count === null`) nu e zero si nu e "nimic de facut": se
 * scrie "—" si "No data yet", in gri, ca un rand linistit. Pana pe 18
 * septembrie 2026 era "?" galben, cu "N of the checks below could not be read -
 * that is not the same as zero" deasupra, si se citea ca eroare - la fel ca pe
 * tablou, unde s-a scos in aceeasi livrare.
 */
export function todayCountLabel(count: number | null): string {
  if (count === null) return '—'
  return count > 999 ? '999+' : String(count)
}

function RowBody({ row }: { row: TodayRow }) {
  const state = todayState(row)
  return (
    <>
      <span className="od-today-icon">
        <i className={`fas ${row.icon}`} aria-hidden="true"></i>
      </span>
      <span className="od-today-n">{todayCountLabel(row.count)}</span>
      <span className="od-today-text">
        <span className="od-today-label">{row.label}</span>
        <span className="od-today-about">
          {row.count === null ? 'No data yet - open it to see for yourself.' : row.about}
        </span>
      </span>
      <i className={`fas fa-chevron-right od-today-go`} aria-hidden="true"></i>
      <span hidden data-state={state}></span>
    </>
  )
}

export function TodayPage() {
  const q = useQuery(todayRowsQuery)
  const rows = q.data?.rows ?? []
  let open = 0
  let unknown = 0
  for (const row of rows) {
    if (row.count === null) unknown += 1
    else open += row.count
  }

  return (
    <Content>
      <style>{TODAY_STYLE}</style>
      <div className="od-today">
        {open === 0 && unknown === 0 && rows.length > 0 && (
          <div className="od-today-clear">
            <i className="fas fa-check-circle" aria-hidden="true"></i>
            <div>
              <b>Nothing is waiting for you.</b>
              No open alarms, no unauthorised changes, no agent gone quiet, and
              nothing new found on the network.
            </div>
          </div>
        )}
        {(open > 0 || unknown > 0) && (
          <p className="od-today-lead">
            {open > 0 ? (
              <>
                <b>{open}</b> thing{open === 1 ? '' : 's'} want something from you.
              </>
            ) : (
              <>Nothing countable is waiting for you.</>
            )}
          </p>
        )}

        <div className="od-today-rows">
          {rows.map((row) => {
            const href = hrefOf(row.url)
            const cls = `od-today-row is-${todayState(row)}`
            if (!href) {
              return (
                <div className={cls} key={row.key} data-key={row.key}>
                  <RowBody row={row} />
                </div>
              )
            }
            return (
              <a className={cls} href={href} key={row.key} data-key={row.key}>
                <RowBody row={row} />
              </a>
            )
          })}
        </div>
      </div>
    </Content>
  )
}
